'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState, type FormEvent } from 'react';

import { BoutiquePickup } from '@/components/checkout/BoutiquePickup';
import { RadioInput } from '@/components/ui/RadioInput';
import { RepairRequestReturnMethodSummary } from '@/features/service/repair-request/RepairRequestReturnMethodSummary';
import { getCountryFromLocale } from '@/lib/i18n';
import type { RepairDetails } from '@/lib/service/ecs/contracts';
import {
  getRepairLogisticsCapabilities,
  type RepairReturnMethod,
} from '@/lib/service/ecs/logisticsCapabilities';
import type { RepairDetailsReturnMethodValues } from '@/lib/service/repairDetailsUpdate';

import { RepairDetailsSection } from './RepairDetailsSection';
import styles from './RepairDetailsSection.module.scss';

interface RepairDetailsReturnMethodSectionProps {
  repair: RepairDetails;
  canChange: boolean;
  isProcessing: boolean;
  onSubmit: (values: RepairDetailsReturnMethodValues) => Promise<void>;
}

export function RepairDetailsReturnMethodSection({
  repair,
  canChange,
  isProcessing,
  onSubmit,
}: Readonly<RepairDetailsReturnMethodSectionProps>) {
  const locale = useLocale();
  const tPersonal = useTranslations('service.repairRequest.personal');
  const tCheckout = useTranslations('checkout');
  const countryCode = getCountryFromLocale(locale).toUpperCase();
  const capabilities = useMemo(() => getRepairLogisticsCapabilities(countryCode), [countryCode]);
  const [returnMethod, setReturnMethod] = useState<RepairReturnMethod>(repair.deliveryOption);
  const [pickupBoutiqueId, setPickupBoutiqueId] = useState(repair.pickupBoutiqueTracking ?? '');
  const [boutiqueError, setBoutiqueError] = useState<string | null>(null);

  const pickupBoutique = repair.pickupBoutiqueName
    ? { name: repair.pickupBoutiqueName, street: '', city: '' }
    : null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (returnMethod === 'boutiquePickup' && !pickupBoutiqueId) {
      setBoutiqueError(tCheckout('delivery.requiredField'));
      return;
    }
    setBoutiqueError(null);
    await onSubmit({ returnMethod, pickupBoutiqueId });
  };

  return (
    <RepairDetailsSection
      title={tPersonal('returnMethod.title')}
      testId="repair-details-return"
      canChange={canChange}
      isProcessing={isProcessing}
      summary={
        <RepairRequestReturnMethodSummary
          returnMethod={repair.deliveryOption}
          serviceBoutique={pickupBoutique}
        />
      }
    >
      {({ onCancel }) => (
        <form
          id="repair-details-return-form"
          data-testid="repair-details-return-form"
          noValidate
          onSubmit={(event) => {
            void handleSubmit(event).catch(() => undefined);
          }}
          onReset={onCancel}
        >
          <p className={styles.sectionHint}>{tPersonal('returnMethod.hintAmend')}</p>
          <div className={styles.optionRow}>
            {capabilities.allowReceiveAtHome ? (
              <RadioInput
                name="returnMethod"
                value="clientShipping"
                checked={returnMethod === 'clientShipping'}
                onChange={() => setReturnMethod('clientShipping')}
                label={tPersonal('returnMethod.homeDelivery')}
                disabled={isProcessing}
              />
            ) : null}
            {capabilities.allowRetrieveInBoutique ? (
              <RadioInput
                name="returnMethod"
                value="boutiquePickup"
                checked={returnMethod === 'boutiquePickup'}
                onChange={() => setReturnMethod('boutiquePickup')}
                label={tPersonal('returnMethod.boutiquePickup')}
                disabled={isProcessing}
              />
            ) : null}
          </div>
          {returnMethod === 'boutiquePickup' ? (
            <BoutiquePickup
              lang={locale}
              value={pickupBoutiqueId}
              onChange={setPickupBoutiqueId}
              error={boutiqueError ?? undefined}
            />
          ) : null}
        </form>
      )}
    </RepairDetailsSection>
  );
}
